import { useEffect } from 'react';
import { RequestsPanel } from '../Requests';
import { Chores } from './Chores';
import { Inbox } from './Inbox';
import { Plan } from './Plan';
import { Today } from './Today';
import { Week } from './Week';
import { DecisionsRail } from './Decisions';
import { SubTabs, SubPanel, type SubTabDef } from '../../components/SubTabs';
import { Tooltip } from '../../ui/Tooltip';
import './requests.css';
import './chores.css';
import './today.css';
/* Card DfhelObKj9iP — direct UI names the partner (Jonathan 2026-10-30). */
import { partnerLabel } from '../../lib/partnerName';

/** Deep-link intent from the compose menu (design v1.1 §5.2): Ask opens the
 *  request composer. */
export interface LifeComposeIntent {
  kind: 'ask';
}

/**
 * Life's sub-navigation (card sjFxIbWi3UgN, Jonathan 2026-10-07): the same
 * top row Notebook carries, so a screen's sections are tabs, not one long
 * stack. Restructured as a consolidated dashboard: Today (the landing tab —
 * what needs you and what is coming up, merged across asks, decisions,
 * household items, sync tasks and dates), Week (the calendar table), Plan
 * (the weekly sync), Household (the working list of shared tasks), Asks &
 * decisions (the rails). Inbox leaves the row — it stays reachable from the
 * More menu and #life-admin, and its unreviewed mail is summarised on Today.
 *
 * Tab keys are unchanged ('chores', 'requests', 'admin', 'plan') so every
 * walk selector, panel id and deep link keeps resolving; only labels and
 * order moved.
 *
 * Naming (founder email 2026-11-04): the catch-all tab is "Requests" — asks
 * and decisions are both requests between the pair — and Inbox is a sub-tab
 * of it rather than a door in the More menu, so all the things that arrive
 * for you sit together.
 */
const LIFE_TABS: SubTabDef[] = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'Week' },
  { key: 'plan', label: 'Plan' },
  { key: 'chores', label: 'Household' },
  { key: 'requests', label: 'Requests' },
  { key: 'admin', label: 'Inbox' },
];

/**
 * Life panel — "Run the household". Today is the consolidated dashboard and
 * the landing tab; the working surfaces sit one tap behind it. The tab state
 * lives in App, not here: the panel unmounts every time the bottom bar
 * switches away, so keeping it locally would drop you back on Today every
 * visit — and the More menu's quick links have to be able to select the tab
 * before it scrolls to it.
 *
 * Mirrors §5.5 of the design doc.
 */
export function LifePanel({
  intent,
  activeTab,
  onTabChange,
  members,
}: {
  intent?: LifeComposeIntent | null;
  activeTab: string;
  onTabChange: (key: string) => void;
  /** Needed by the Inbox tab: a member id reads as a display name on the routed-to row. */
  members: { memberId: string; displayName: string }[];
}) {
  // A compose deep-link targets the asks lane — select it rather than
  // silently dropping the intent on whatever tab was active.
  useEffect(() => {
    if (intent?.kind === 'ask' && activeTab !== 'requests') onTabChange('requests');
  }, [intent, activeTab, onTabChange]);

  return (
    <>
      <SubTabs
        name="life"
        label="Life sections"
        tabs={LIFE_TABS}
        active={activeTab}
        onChange={onTabChange}
        sticky
      />
      <SubPanel name="life" tabKey={activeTab}>

        {activeTab === 'today' && <Today onOpen={onTabChange} />}

        {activeTab === 'week' && <Week />}

        {activeTab === 'requests' && (
          <>
            {/* Jonathan 2026-10-24 (card ifd8sEdMo0JA): the first-move paragraph
                is gone from the layout — it rendered above the lane even when
                the lane was empty and pushed the user's own requests down the
                screen. What the paragraph used to explain now lives in a (?)
                tooltip beside the heading, so nothing here takes a line unless
                it is asked for. */}
            <h2 className="section-title" style={{ fontFamily: 'var(--font-heading)', fontSize: 'var(--type-h3-size)', letterSpacing: 'var(--type-tracking-wider)', fontWeight: 400, color: 'var(--color-cream)', marginTop: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              Requests
              <Tooltip label={`Use Requests to ask a favour of ${partnerLabel()}, where they can incorporate into their plans.`}>
              </Tooltip>
            </h2>
            <div id="life-lane" className="life-lane">
              <RequestsPanel
                intent={intent?.kind === 'ask' ? { kind: 'ask' } : null}
                lane="life"
                hideDone
              />
            </div>
            {/* Decisions beside Requests (parent card VcBi3nr9GjBj, child C
                bwcTJh8Od5rk): the couple-authored two-option choices, in the
                same rail language as the asks above them. Open rows first,
                settled rows below — never mixed, so a resolved decision cannot
                read as work still owed. */}
            <DecisionsRail />
          </>
        )}

        {activeTab === 'chores' && <Chores />}

        {/* The couple's inbox (card tlwgXSkiimS2, renamed from 'Admin' by card
            NWmSqIsojQyU) — a sub-tab again by the founder's call of 2026-11-04,
            sitting beside Requests so everything that arrives for you is in one
            place; #life-admin still deep-links straight to it. */}
        {activeTab === 'admin' && <Inbox members={members} />}

        {/* Plan (card 7aTCRQ9j8PWV): the weekly meeting — record, draft, confirm. */}
        {activeTab === 'plan' && <Plan />}

      </SubPanel>

    </>
  );
}
